import { GETREQUESTOPTIONS, instance } from '../apiService';

export type ActorScope = 'global' | 'department' | 'manufacturer' | 'nordinsight';

export interface ActorWithCount {
    _id: string;
    role: string;
    description?: string;
    scope?: ActorScope;
    rights: string[];
    deptPermissions: string[];
    alternativeNames?: Record<string, string>;
    alternativeDescriptions?: Record<string, string>;
    userCount: number;
}

export interface ActorUser {
    _id: string;
    name: string | null;
    email: string;
    deptPermissions: string[];
    expiryDate: string | null;
}

export interface ActorDepartmentGroup {
    department: { _id: string; name: string; end_date?: string | null };
    /** The permissions users of this role are expected to have here. */
    templatePermissions: string[];
    /** 'department' = custom department template; 'actor' = NordInsight managed. */
    templateSource: 'department' | 'actor';
    users: ActorUser[];
}

export interface ActorUsersResponse {
    departments: ActorDepartmentGroup[];
}

export const getActors = async (
    activeDeptsOnly = false,
    scopeFilter?: string
): Promise<ActorWithCount[]> => {
    const searchParams = new URLSearchParams();
    if (activeDeptsOnly) searchParams.set('activeOnly', 'true');
    if (scopeFilter) searchParams.set('scopeFilter', scopeFilter);
    const qs = searchParams.toString();
    const res = await instance.get(`/api/actor/${qs ? `?${qs}` : ''}`, GETREQUESTOPTIONS());
    return res.data;
};

export const createActor = async (
    actor: Omit<ActorUpdate, 'role'> & { role: string }
): Promise<ActorWithCount> => {
    const res = await instance.post('/api/actor/add', actor, GETREQUESTOPTIONS());
    return res.data;
};

// Role names are fixed after creation, so updates never carry `role`.
export interface ActorUpdate {
    description?: string;
    scope?: ActorScope;
    rights?: string[];
    deptPermissions?: string[];
    alternativeNames?: Record<string, string>;
    alternativeDescriptions?: Record<string, string>;
}

export const updateActor = async (
    id: string,
    updates: ActorUpdate
): Promise<ActorWithCount> => {
    const res = await instance.put(
        `/api/actor/${id}`,
        updates,
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export const deleteActor = async (id: string): Promise<void> => {
    await instance.delete(`/api/actor/${id}`, GETREQUESTOPTIONS());
};

export const migrateActorUsers = async (
    sourceActorId: string,
    targetActorId: string
): Promise<{ migratedCount: number; emails: string[] }> => {
    const res = await instance.post(
        '/api/actor/migrate-users',
        { sourceActorId, targetActorId },
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export const getActorUsers = async (
    id: string
): Promise<ActorUsersResponse> => {
    const res = await instance.get(
        `/api/actor/${id}/users`,
        GETREQUESTOPTIONS()
    );
    return res.data;
};

